import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, BadgeCheck } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import CTABand from '../components/CTABand';
import { Reveal, Chapter } from '../components/motion';
import { IMAGES, localBusinessSchema, breadcrumbSchema } from '../data/site';
import { PRODUCT_CATEGORIES } from '../data/products';

export default function Products() {
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'American Standard HVAC Products',
    itemListElement: PRODUCT_CATEGORIES.flatMap((c) =>
      c.products.map((p) => ({ '@type': 'Product', name: `American Standard ${p.name}`, brand: 'American Standard' }))
    ),
  };

  return (
    <>
      <SEO
        title="American Standard Heating & Air Products | B.E. Holbrooks Co. | Stanfield, NC"
        description="Browse American Standard air conditioners, heat pumps, furnaces, packaged systems, ductless mini-splits, and indoor air quality products installed by B.E. Holbrooks Co. in Stanfield, NC. Free estimates: 704-888-2411."
        path="/products"
        schemas={[
          localBusinessSchema,
          itemListSchema,
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Products', path: '/products' }]),
        ]}
      />

      <PageHero
        eyebrow="American Standard Customer Care Dealer"
        h1Lines={['Energy Efficient And Reliable HVAC Equipment']}
        sub="From high-efficiency air conditioners and heat pumps to furnaces, ductless systems, and whole-home air quality — we install and service the full American Standard lineup across Stanfield and the greater Charlotte area."
        image={IMAGES.techAmericanStandard}
        imageAlt="B.E. Holbrooks Co. technician installing an American Standard Platinum system in Stanfield, NC"
        crumbs={[{ name: 'Products' }]}
      />

      {/* CATEGORY JUMP NAV */}
      <section className="border-b border-slate-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal className="flex flex-wrap items-center gap-2" data-testid="product-jump-nav">
            {PRODUCT_CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                data-testid={`jump-${c.id}`}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 font-display text-xs font-bold text-slate-600 transition-all hover:border-brand hover:text-brand"
              >
                {c.h2}
              </a>
            ))}
          </Reveal>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      {PRODUCT_CATEGORIES.map((cat, ci) => (
        <section
          key={cat.id}
          id={cat.id}
          className={`scroll-mt-32 py-16 lg:py-20 ${ci % 2 === 0 ? 'bg-white' : 'bg-brand-soft/40'}`}
          data-testid={`category-${cat.id}`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
            <Reveal>
              <Chapter num={String(ci + 1).padStart(2, '0')} label="Product Line" />
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">{cat.h2}</h2>
              <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">{cat.intro}</p>
            </Reveal>
            <div className={`mt-10 grid gap-6 ${cat.products.length === 1 ? 'lg:max-w-xl' : cat.products.length === 2 ? 'lg:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {cat.products.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.1}>
                  <article
                    className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-xl"
                    data-testid={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <div className="flex h-52 items-center justify-center bg-slate-50 p-4">
                      <img
                        src={p.img}
                        alt={p.alt}
                        loading="lazy"
                        className={`h-full w-full ${p.cover ? 'object-cover rounded-lg' : 'object-contain'}`}
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-brand">{p.tag}</p>
                      <h3 className="mt-1.5 font-display text-lg font-bold text-navy">{p.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                      <ul className="mt-4 flex-1 space-y-2">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm font-medium text-slate-700">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-trustgreen" /> {f}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contact?form=install"
                        data-testid={`product-estimate-${cat.id}-${i}`}
                        className="mt-5 inline-flex items-center gap-1.5 font-display text-sm font-bold text-brand transition-colors hover:text-brand-dark"
                      >
                        Get a Free Estimate <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* DEALER NOTE */}
      <section className="bg-white pb-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-brand-soft/50 p-6" data-testid="dealer-note">
              <BadgeCheck className="mt-0.5 h-6 w-6 shrink-0 text-brand" />
              <p className="text-sm leading-relaxed text-slate-600">
                <strong className="text-navy">A note from our team:</strong> Product availability, efficiency ratings,
                and specifications vary by model. During your free in-home estimate, we will confirm the right American
                Standard system for your home, budget, and comfort goals — including current rebates and financing.
                B.E. Holbrooks Co. is an independent American Standard Customer Care Dealer.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        heading="Not Sure Which System Fits Your Home?"
        text="That is exactly what our free comfort consultation is for. Call 704-888-2411 or request your free install estimate online."
        testid="cta-products"
      />
    </>
  );
}
